import React from "react"
import {Link} from "react-router-dom";

import globalStyles from "../../../../App.module.scss";

export default function SshTunneling() {
    return (
        <div className={globalStyles.article}>
            <h1>SSH tunneling / port forwarding</h1>

            <p>SSH can forward TCP traffic through an encrypted connection, which is a quick way to reach a
                service that isn't (or shouldn't be) exposed directly. Complements the <b>SSH key auth</b>
                page.</p>

            <h3>Local forwarding (-L)</h3>
            <p>Reach a port on the remote side (or reachable from it) through a local port on your machine.</p>
            <pre className={globalStyles.code}>
    <b>user@host:~$</b> ssh -L 5432:localhost:5432 julian@db-bastion.example.com<br/>
            </pre>
            <p>Now <code>localhost:5432</code> on your machine forwards to port 5432 on <b>db-bastion</b> —
                handy for connecting a local DB client to a database that only listens on a jump host's loopback
                interface.</p>

            <h3>Remote forwarding (-R)</h3>
            <p>The opposite: expose a port on your local machine to the remote server (and whoever can reach
                it).</p>
            <pre className={globalStyles.code}>
    <b>user@host:~$</b> ssh -R 8080:localhost:3000 julian@public-server.example.com<br/>
            </pre>
            <p>Anything connecting to <code>public-server:8080</code> now reaches port 3000 on your local
                machine — useful for demoing a local dev server without deploying it.</p>

            <h3>Dynamic forwarding / SOCKS proxy (-D)</h3>
            <pre className={globalStyles.code}>
    <b>user@host:~$</b> ssh -D 1080 julian@bastion.example.com<br/>
            </pre>
            <p>Turns the SSH connection into a SOCKS5 proxy on local port 1080. Point a browser or
                <code> curl --socks5 localhost:1080 ...</code> at it to route arbitrary traffic through the
                bastion.</p>

            <h3>Useful flags</h3>
            <div className={globalStyles.styledTable}>
                <table style={{minWidth: '330px'}}>
                    <thead>
                    <tr>
                        <th colSpan="2">Flags</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td><b>-N</b></td>
                        <td>Don't execute a remote command — just forward, useful for tunnel-only sessions</td>
                    </tr>
                    <tr>
                        <td><b>-f</b></td>
                        <td>Go to background after connecting</td>
                    </tr>
                    <tr>
                        <td><b>-p 2222</b></td>
                        <td>Connect to a non-default SSH port</td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <pre className={globalStyles.code}>
    <b>user@host:~$</b> ssh -f -N -L 5432:localhost:5432 julian@db-bastion.example.com<br/>
            </pre>

            <h3>Persist a tunnel</h3>
            <p>The canonical <Link to="/ssh-client-config">SSH client configuration (~/.ssh/config)</Link> guide
                includes a loopback-bound <code>LocalForward</code> template with <code>ExitOnForwardFailure</code>
                and the <code>ssh -N</code> command. Keep reusable client settings there rather than maintaining a
                second configuration example on this page.</p>
        </div>
    )
}
